/* eslint-disable */
import { useMutation, useQuery } from '@tanstack/react-query';
import type { QueryKey, UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';

import { courseFetcher } from '../Fetcher';
import type { RequestOptions } from '../Fetcher';
const fetcher = courseFetcher;

export type Maybe<T> = T | null;

export type OneOf<T> = T extends unknown[]
  ? T[number]
  : T extends readonly unknown[]
    ? T[number]
    : T;

export type Exact<T extends { [key: string]: unknown }> = {
  [K in keyof T]-?: Exclude<T[K], undefined>;
};

export type Values<T extends Record<string, unknown>> = T extends Record<string, infer U>
  ? U
  : T extends { [key: string]: infer U }
    ? U
    : never;

export const enum ResponseStatus {
  OK = 'OK',
  SERVER_ERROR = 'SERVER_ERROR',
  DUPLICATE = 'DUPLICATE',
  NOT_FOUND = 'NOT_FOUND',
  BAD_REQUEST = 'BAD_REQUEST',
  BAD_PARAMETER = 'BAD_PARAMETER',
  CONFLICT = 'CONFLICT',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  PAYLOAD_TOO_LARGE = 'PAYLOAD_TOO_LARGE',
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',
}

export type Success<T = unknown> = {
  statusCode: ResponseStatus.OK;
  message: '';
  data: T;
};

export type Error = {
  statusCode: Omit<keyof typeof ResponseStatus, 'OK'>;
  message: string;
  data: Maybe<''>;
};

/**
 * @typeParam T - Response data type
 */
export type ApiResponse<T = unknown> = Success<T>;

export const API_ENDPOINT = {
  CLIENT_V1_CART: '/client/api/v1/cart',
  CLIENT_V1_COURSE_CERTIFICATES: '/client/api/v1/course-certificates',
  CLIENT_V1_MARKETING_PARTNER_LINKS: '/client/api/v1/marketing-partner-links',
  CLIENT_V1_MARKETING_PARTNERS: '/client/api/v1/marketing-partners',
  CLIENT_V1_MY_LISTS: '/client/api/v1/my-lists',
  CLIENT_V1_USER_STATISTICS: '/client/api/v1/user-statistics',
  CLIENT_V1_CATEGORY_MAIN: '/client/api/v1/category/main',
  CLIENT_V1_COURSE_CERTIFICATES_CONFIRM: '/client/api/v1/course-certificates/confirm',
  CLIENT_V1_COURSE_CERTIFICATES_DETAIL: '/client/api/v1/course-certificates/detail',
  CLIENT_V1_COURSE_CERTIFICATES_UNCONFIRMED: '/client/api/v1/course-certificates/unconfirmed',
  CLIENT_V1_COURSE_GIFTS_RECEIVED: '/client/api/v1/course-gifts/received',
  CLIENT_V1_COURSE_GIFTS_SENT: '/client/api/v1/course-gifts/sent',
  CLIENT_V1_COURSE_LIKES: '/client/api/v1/course/likes',
  CLIENT_V1_MY_LISTS_COURSE: '/client/api/v1/my-lists/course',
  CLIENT_V1_MY_LISTS_SEQUENCE: '/client/api/v1/my-lists/sequence',
  CLIENT_V1_MY_LISTS_ID: (id: string) => `/client/api/v1/my-lists/${id}`,
  CLIENT_V1_ORDER_SUMMARIES: '/client/api/v1/order/summaries',
  CLIENT_V1_VOUCHER_ARCHIVE: '/client/api/v1/voucher/archive',
  CLIENT_V2_MY_LISTS_ID: (id: string) => `/client/api/v2/my-lists/${id}`,
  CLIENT_V2_VOUCHERS_ENROLLMENT_VERIFICATIONS: '/client/api/v2/vouchers/enrollment-verifications',
  CLIENT_V2_VOUCHERS_MY: '/client/api/v2/vouchers/my',
  CLIENT_V1_COURSE_CERTIFICATES_CHALLENGE_DETAIL:
    '/client/api/v1/course-certificates/challenge/detail',
  CLIENT_V1_COURSE_USER_USERID: (userId: string) => `/client/api/v1/course/user/${userId}`,
  CLIENT_V1_COURSE_COURSEID_LIKE: (courseId: string) => `/client/api/v1/course/${courseId}/like`,
  CLIENT_V1_COURSES_ID_SIMPLE: (id: string) => `/client/api/v1/courses/${id}/simple`,
  CLIENT_V1_MARKETING_PARTNER_LINKS_CREATE_PREVIEW:
    '/client/api/v1/marketing-partner-links/create/preview',
  CLIENT_V1_MARKETING_PARTNERS_COURSES_SEARCH: '/client/api/v1/marketing-partners/courses/search',
  CLIENT_V1_MY_LISTS_COURSE_SEQUENCE: '/client/api/v1/my-lists/course/sequence',
  CLIENT_V1_ROADMAP_AUTHOR_USERID: (userId: string) => `/client/api/v1/roadmap/author/${userId}`,
  CLIENT_V1_VOUCHERS_ENROLLMENT_VERIFICATIONS_CONFIRM:
    '/client/api/v1/vouchers/enrollment-verifications/confirm',
  CLIENT_V1_VOUCHERS_ID_FREE: (id: string) => `/client/api/v1/vouchers/${id}/free`,
};

export const fetchClientApiV1Cart = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1CartResponse>(url, requestOptions);

export function useClientApiV1CartQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1CartResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}) {
  const result = useQuery<ClientApiV1CartResponse, unknown>(
    queryKey,
    () => fetchClientApiV1Cart(url, requestOptions),
    options,
  );

  return { ...result, data: result.data?.data };
}

/** 클라이언트 장바구니 아이템을 조회한다. */
export type ClientApiV1CartQueryString = Partial<{
  lang: string;
}>;

export type ClientApiV1CartResponse = FindClientCartResponse;

export function useClientApiV1CartCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1CartCreateResponse,
    unknown,
    ClientApiV1CartCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1CartCreateResponse,
    unknown,
    ClientApiV1CartCreateVariables,
    TContext
  >((variables) => fetcher.post(url, variables, requestOptions), options);

  return { ...result, data: result.data?.data };
}

export type ClientApiV1CartCreateVariables = AddCartsRequest;

export type ClientApiV1CartCreateResponse = AddCartsResponse;

export function useClientApiV1CartDeleteMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1CartDeleteResponse,
    unknown,
    ClientApiV1CartDeleteVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1CartDeleteResponse,
    unknown,
    ClientApiV1CartDeleteVariables,
    TContext
  >((variables) => fetcher.delete(url, variables, requestOptions), options);

  return { ...result, data: result.data?.data };
}

export type ClientApiV1CartDeleteVariables = RemoveCartsRequest;

export type ClientApiV1CartDeleteResponse = EmptyResponse;

export const fetchClientApiV1CourseCertificates = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CourseCertificatesResponse>(url, requestOptions);

export function useClientApiV1CourseCertificatesQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CourseCertificatesResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useQuery<ClientApiV1CourseCertificatesResponse, unknown>(
    queryKey,
    () => fetchClientApiV1CourseCertificates(url, requestOptions),
    options,
  );

  return { ...result, data: result.data?.data };
}

/** 사용자의 수료증 목록을 조회한다 */
export type ClientApiV1CourseCertificatesQueryString = Partial<{
  userId: number;
  isArchived: boolean;
  sort: 'CERTIFICATED' | 'CONFIRMED';
  lang: string;
}>;

export type ClientApiV1CourseCertificatesResponse = GetCertificatesResponse;

export function useClientApiV1MarketingPartnerLinksCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MarketingPartnerLinksCreateResponse,
    unknown,
    ClientApiV1MarketingPartnerLinksCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MarketingPartnerLinksCreateResponse,
    unknown,
    ClientApiV1MarketingPartnerLinksCreateVariables,
    TContext
  >((variables) => fetcher.post(url, variables, requestOptions), options);

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MarketingPartnerLinksCreateVariables = CreateMarketingPartnerLinkRequest;

export type ClientApiV1MarketingPartnerLinksCreateResponse = CreateMarketingPartnerLinkResponse;

export function useClientApiV1MarketingPartnersCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MarketingPartnersCreateResponse,
    unknown,
    ClientApiV1MarketingPartnersCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MarketingPartnersCreateResponse,
    unknown,
    ClientApiV1MarketingPartnersCreateVariables,
    TContext
  >((variables) => fetcher.post(url, variables, requestOptions), options);

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MarketingPartnersCreateVariables = SignupMarketingPartnerRequest;

export type ClientApiV1MarketingPartnersCreateResponse = SignupMarketingPartnerResponse;

export const fetchClientApiV1MyLists = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1MyListsResponse>(url, requestOptions);

export function useClientApiV1MyListsQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1MyListsResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}) {
  const result = useQuery<ClientApiV1MyListsResponse, unknown>(
    queryKey,
    () => fetchClientApiV1MyLists(url, requestOptions),
    options,
  );

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MyListsResponse = FindMyListResponse;

export function useClientApiV1MyListsUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MyListsUpdateResponse,
    unknown,
    ClientApiV1MyListsUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MyListsUpdateResponse,
    unknown,
    ClientApiV1MyListsUpdateVariables,
    TContext
  >((variables) => fetcher.put(url, variables, requestOptions), options);

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MyListsUpdateVariables = UpdateMyListRequest;

export type ClientApiV1MyListsUpdateResponse = EmptyResponse;

export function useClientApiV1MyListsCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MyListsCreateResponse,
    unknown,
    ClientApiV1MyListsCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MyListsCreateResponse,
    unknown,
    ClientApiV1MyListsCreateVariables,
    TContext
  >((variables) => fetcher.post(url, variables, requestOptions), options);

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MyListsCreateVariables = CreateMyListRequest;

export type ClientApiV1MyListsCreateResponse = EmptyResponse;

export const fetchClientApiV1UserStatistics = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1UserStatisticsResponse>(url, requestOptions);

export function useClientApiV1UserStatisticsQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1UserStatisticsResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useQuery<ClientApiV1UserStatisticsResponse, unknown>(
    queryKey,
    () => fetchClientApiV1UserStatistics(url, requestOptions),
    options,
  );

  return { ...result, data: result.data?.data };
}

export type ClientApiV1UserStatisticsResponse = UserStatisticsResponse;

export const fetchClientApiV1CategoryMain = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1CategoryMainResponse>(url, requestOptions);

export function useClientApiV1CategoryMainQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1CategoryMainResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}) {
  const result = useQuery<ClientApiV1CategoryMainResponse, unknown>(
    queryKey,
    () => fetchClientApiV1CategoryMain(url, requestOptions),
    options,
  );

  return { ...result, data: result.data?.data };
}

export type ClientApiV1CategoryMainResponse = FindParentCategoryResponse;

export function useClientApiV1CourseCertificatesConfirmUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1CourseCertificatesConfirmUpdateResponse,
    unknown,
    ClientApiV1CourseCertificatesConfirmUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1CourseCertificatesConfirmUpdateResponse,
    unknown,
    ClientApiV1CourseCertificatesConfirmUpdateVariables,
    TContext
  >((variables) => fetcher.put(url, variables, requestOptions), options);

  return { ...result, data: result.data?.data };
}

export type ClientApiV1CourseCertificatesConfirmUpdateVariables = ConfirmCourseCertificateRequestV2;

export type ClientApiV1CourseCertificatesConfirmUpdateResponse = ConfirmCourseCertificateResponse;

export const fetchClientApiV1CourseCertificatesDetail = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CourseCertificatesDetailResponse>(url, requestOptions);

export function useClientApiV1CourseCertificatesDetailQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CourseCertificatesDetailResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useQuery<ClientApiV1CourseCertificatesDetailResponse, unknown>(
    queryKey,
    () => fetchClientApiV1CourseCertificatesDetail(url, requestOptions),
    options,
  );

  return { ...result, data: result.data?.data };
}

/** 수료증 상세 정보를 조회한다 */
export type ClientApiV1CourseCertificatesDetailQueryString = Partial<{
  userId: number;
  courseId: number;
  voucherId: number;
  lang: string;
}>;

export type ClientApiV1CourseCertificatesDetailResponse = GetCertificateDetailResponse;

export const fetchClientApiV1CourseCertificatesUnconfirmed = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CourseCertificatesUnconfirmedResponse>(url, requestOptions);

export function useClientApiV1CourseCertificatesUnconfirmedQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CourseCertificatesUnconfirmedResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useQuery<ClientApiV1CourseCertificatesUnconfirmedResponse, unknown>(
    queryKey,
    () => fetchClientApiV1CourseCertificatesUnconfirmed(url, requestOptions),
    options,
  );

  return { ...result, data: result.data?.data };
}

export type ClientApiV1CourseCertificatesUnconfirmedResponse = UnconfirmedResponse;

export const fetchClientApiV1CourseGiftsReceived = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CourseGiftsReceivedResponse>(url, requestOptions);

export function useClientApiV1CourseGiftsReceivedQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CourseGiftsReceivedResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useQuery<ClientApiV1CourseGiftsReceivedResponse, unknown>(
    queryKey,
    () => fetchClientApiV1CourseGiftsReceived(url, requestOptions),
    options,
  );

  return { ...result, data: result.data?.data };
}

/** 받은 선물 내역 리스트를 조회한다 */
export type ClientApiV1CourseGiftsReceivedQueryString = Partial<{
  userId: number;
  pageNumber: number;
  pageSize: number;
}>;

export type ClientApiV1CourseGiftsReceivedResponse = FindReceivedCourseGiftResponse;

export const fetchClientApiV1CourseGiftsSent = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CourseGiftsSentResponse>(url, requestOptions);

export function useClientApiV1CourseGiftsSentQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CourseGiftsSentResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useQuery<ClientApiV1CourseGiftsSentResponse, unknown>(
    queryKey,
    () => fetchClientApiV1CourseGiftsSent(url, requestOptions),
    options,
  );

  return { ...result, data: result.data?.data };
}

/** 보낸 강의 선물 내역 리스트를 조회한다 */
export type ClientApiV1CourseGiftsSentQueryString = Partial<{
  pageNumber: number;
  pageSize: number;
}>;

export type ClientApiV1CourseGiftsSentResponse = FindSentCourseGiftResponse;

export const fetchClientApiV1CourseLikes = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1CourseLikesResponse>(url, requestOptions);

export function useClientApiV1CourseLikesQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1CourseLikesResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}) {
  const result = useQuery<ClientApiV1CourseLikesResponse, unknown>(
    queryKey,
    () => fetchClientApiV1CourseLikes(url, requestOptions),
    options,
  );

  return { ...result, data: result.data?.data };
}

/** 좋아요한 강의 카드 리스트 조회 */
export type ClientApiV1CourseLikesQueryString = Partial<{
  sort: 'RECENT' | 'TITLE' | 'STUDENT' | 'RATING';
  chargeType: 'FREE' | 'PAID' | 'ALL';
  lang: string;
}>;

export type ClientApiV1CourseLikesResponse = CourseByLikesResponse;

export const fetchClientApiV1MyListsCourse = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1MyListsCourseResponse>(url, requestOptions);

export function useClientApiV1MyListsCourseQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1MyListsCourseResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useQuery<ClientApiV1MyListsCourseResponse, unknown>(
    queryKey,
    () => fetchClientApiV1MyListsCourse(url, requestOptions),
    options,
  );

  return { ...result, data: result.data?.data };
}

/** 강의 기반 강의폴더 조회 */
export type ClientApiV1MyListsCourseQueryString = Partial<{
  courseId: number;
}>;

export type ClientApiV1MyListsCourseResponse = FindMyListByCourseResponse;

export function useClientApiV1MyListsCourseCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MyListsCourseCreateResponse,
    unknown,
    ClientApiV1MyListsCourseCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MyListsCourseCreateResponse,
    unknown,
    ClientApiV1MyListsCourseCreateVariables,
    TContext
  >((variables) => fetcher.post(url, variables, requestOptions), options);

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MyListsCourseCreateVariables = AddCourseRequest;

export type ClientApiV1MyListsCourseCreateResponse = EmptyResponse;

export function useClientApiV1MyListsCourseDeleteMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MyListsCourseDeleteResponse,
    unknown,
    ClientApiV1MyListsCourseDeleteVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MyListsCourseDeleteResponse,
    unknown,
    ClientApiV1MyListsCourseDeleteVariables,
    TContext
  >((variables) => fetcher.delete(url, variables, requestOptions), options);

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MyListsCourseDeleteVariables = RemoveCourseRequest;

export type ClientApiV1MyListsCourseDeleteResponse = EmptyResponse;

export function useClientApiV1MyListsSequenceCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MyListsSequenceCreateResponse,
    unknown,
    ClientApiV1MyListsSequenceCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MyListsSequenceCreateResponse,
    unknown,
    ClientApiV1MyListsSequenceCreateVariables,
    TContext
  >((variables) => fetcher.post(url, variables, requestOptions), options);

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MyListsSequenceCreateVariables = UpdateMyListsSequenceRequest;

export type ClientApiV1MyListsSequenceCreateResponse = EmptyResponse;

export const fetchClientApiV1MyListsId = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1MyListsIdResponse>(url, requestOptions);

export function useClientApiV1MyListsIdQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1MyListsIdResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}) {
  const result = useQuery<ClientApiV1MyListsIdResponse, unknown>(
    queryKey,
    () => fetchClientApiV1MyListsId(url, requestOptions),
    options,
  );

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MyListsIdResponse = FindMyListCoursesResponse;

export function useClientApiV1MyListsIdDeleteMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MyListsIdDeleteResponse,
    unknown,
    ClientApiV1MyListsIdDeleteVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MyListsIdDeleteResponse,
    unknown,
    ClientApiV1MyListsIdDeleteVariables,
    TContext
  >((variables) => fetcher.delete(url, variables, requestOptions), options);

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MyListsIdDeleteResponse = EmptyResponse;

export type ClientApiV1MyListsIdDeleteVariables = unknown;

export const fetchClientApiV1OrderSummaries = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1OrderSummariesResponse>(url, requestOptions);

export function useClientApiV1OrderSummariesQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1OrderSummariesResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useQuery<ClientApiV1OrderSummariesResponse, unknown>(
    queryKey,
    () => fetchClientApiV1OrderSummaries(url, requestOptions),
    options,
  );

  return { ...result, data: result.data?.data };
}

export type ClientApiV1OrderSummariesResponse = FindOrderSummaryResponse;

export function useClientApiV1VoucherArchiveUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1VoucherArchiveUpdateResponse,
    unknown,
    ClientApiV1VoucherArchiveUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1VoucherArchiveUpdateResponse,
    unknown,
    ClientApiV1VoucherArchiveUpdateVariables,
    TContext
  >((variables) => fetcher.put(url, variables, requestOptions), options);

  return { ...result, data: result.data?.data };
}

export type ClientApiV1VoucherArchiveUpdateVariables = ArchiveVoucherV2Request;

export type ClientApiV1VoucherArchiveUpdateResponse = EmptyResponse;

export const fetchClientApiV2MyListsId = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV2MyListsIdResponse>(url, requestOptions);

export function useClientApiV2MyListsIdQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV2MyListsIdResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}) {
  const result = useQuery<ClientApiV2MyListsIdResponse, unknown>(
    queryKey,
    () => fetchClientApiV2MyListsId(url, requestOptions),
    options,
  );

  return { ...result, data: result.data?.data };
}

/** 강의폴더에 속한 강의 리스트 조회 */
export type ClientApiV2MyListsIdQueryString = Partial<{
  lang: string;
}>;

export type ClientApiV2MyListsIdResponse = FindMyListCoursesResponse;

export const fetchClientApiV2VouchersEnrollmentVerifications = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV2VouchersEnrollmentVerificationsResponse>(url, requestOptions);

export function useClientApiV2VouchersEnrollmentVerificationsQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV2VouchersEnrollmentVerificationsResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useQuery<ClientApiV2VouchersEnrollmentVerificationsResponse, unknown>(
    queryKey,
    () => fetchClientApiV2VouchersEnrollmentVerifications(url, requestOptions),
    options,
  );

  return { ...result, data: result.data?.data };
}

/** 수강확인증 목록 조회 API */
export type ClientApiV2VouchersEnrollmentVerificationsQueryString = Partial<{
  userId: number;
  lang: string;
}>;

export type ClientApiV2VouchersEnrollmentVerificationsResponse = GetEnrollmentVerificationResponse;

export const fetchClientApiV2VouchersMy = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV2VouchersMyResponse>(url, requestOptions);

export function useClientApiV2VouchersMyQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV2VouchersMyResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}) {
  const result = useQuery<ClientApiV2VouchersMyResponse, unknown>(
    queryKey,
    () => fetchClientApiV2VouchersMy(url, requestOptions),
    options,
  );

  return { ...result, data: result.data?.data };
}

/** 오프셋기반 페이지네이션 보유한 강의 조회 API */
export type ClientApiV2VouchersMyQueryString = Partial<{
  userId: number;
  searchKeyword: string;
  sort: 'COURSE_TITLE' | 'RECENT_STUDYING' | 'RECENT_APPLY' | 'EARLIEST_EXPIRATION';
  courseType: 'ON' | 'OFF' | 'CHALLENGE';
  studying: 'STUDYING' | 'COMPLETED';
  isReviewable: boolean;
  isExcludeExpired: boolean;
  isArchived: boolean;
  pageNumber: number;
  pageSize: number;
  lang: string;
}>;

export type ClientApiV2VouchersMyResponse = MyVoucherDto;

export const fetchClientApiV1CourseCertificatesChallengeDetail = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CourseCertificatesChallengeDetailResponse>(url, requestOptions);

export function useClientApiV1CourseCertificatesChallengeDetailQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CourseCertificatesChallengeDetailResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useQuery<ClientApiV1CourseCertificatesChallengeDetailResponse, unknown>(
    queryKey,
    () => fetchClientApiV1CourseCertificatesChallengeDetail(url, requestOptions),
    options,
  );

  return { ...result, data: result.data?.data };
}

/** 챌린지 수료증 상세 정보를 조회한다 */
export type ClientApiV1CourseCertificatesChallengeDetailQueryString = Partial<{
  userId: number;
  courseId: number;
  voucherId: number;
  lang: string;
}>;

export type ClientApiV1CourseCertificatesChallengeDetailResponse = GetCertificateDetailResponse;

export const fetchClientApiV1CourseUserUserId = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CourseUserUserIdResponse>(url, requestOptions);

export function useClientApiV1CourseUserUserIdQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CourseUserUserIdResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useQuery<ClientApiV1CourseUserUserIdResponse, unknown>(
    queryKey,
    () => fetchClientApiV1CourseUserUserId(url, requestOptions),
    options,
  );

  return { ...result, data: result.data?.data };
}

/** 유저 프로필 강의리스트 조회 */
export type ClientApiV1CourseUserUserIdQueryString = Partial<{
  pageNumber: number;
  pageSize: number;
  sort: string;
  lang: string;
}>;

export type ClientApiV1CourseUserUserIdResponse = UserProfileCourseResponse;

export function useClientApiV1CourseCourseIdLikeCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1CourseCourseIdLikeCreateResponse,
    unknown,
    ClientApiV1CourseCourseIdLikeCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1CourseCourseIdLikeCreateResponse,
    unknown,
    ClientApiV1CourseCourseIdLikeCreateVariables,
    TContext
  >((variables) => fetcher.post(url, variables, requestOptions), options);

  return { ...result, data: result.data?.data };
}

export type ClientApiV1CourseCourseIdLikeCreateResponse = EmptyResponse;

export type ClientApiV1CourseCourseIdLikeCreateVariables = unknown;

export function useClientApiV1CourseCourseIdLikeDeleteMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1CourseCourseIdLikeDeleteResponse,
    unknown,
    ClientApiV1CourseCourseIdLikeDeleteVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1CourseCourseIdLikeDeleteResponse,
    unknown,
    ClientApiV1CourseCourseIdLikeDeleteVariables,
    TContext
  >((variables) => fetcher.delete(url, variables, requestOptions), options);

  return { ...result, data: result.data?.data };
}

export type ClientApiV1CourseCourseIdLikeDeleteResponse = EmptyResponse;

export type ClientApiV1CourseCourseIdLikeDeleteVariables = unknown;

export const fetchClientApiV1CoursesIdSimple = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CoursesIdSimpleResponse>(url, requestOptions);

export function useClientApiV1CoursesIdSimpleQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CoursesIdSimpleResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useQuery<ClientApiV1CoursesIdSimpleResponse, unknown>(
    queryKey,
    () => fetchClientApiV1CoursesIdSimple(url, requestOptions),
    options,
  );

  return { ...result, data: result.data?.data };
}

export type ClientApiV1CoursesIdSimpleResponse = CourseSimpleOneResponse;

export const fetchClientApiV1MarketingPartnerLinksCreatePreview = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1MarketingPartnerLinksCreatePreviewResponse>(url, requestOptions);

export function useClientApiV1MarketingPartnerLinksCreatePreviewQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1MarketingPartnerLinksCreatePreviewResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useQuery<ClientApiV1MarketingPartnerLinksCreatePreviewResponse, unknown>(
    queryKey,
    () => fetchClientApiV1MarketingPartnerLinksCreatePreview(url, requestOptions),
    options,
  );

  return { ...result, data: result.data?.data };
}

/** 마케팅 파트너 홍보링크 생성 미리보기를 조회한다 */
export type ClientApiV1MarketingPartnerLinksCreatePreviewQueryString = Partial<{
  courseId: number;
  isAdpickMarketer: boolean;
}>;

export type ClientApiV1MarketingPartnerLinksCreatePreviewResponse =
  GetMarketingPartnerLinkCreatePreviewResponse;

export const fetchClientApiV1MarketingPartnersCoursesSearch = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1MarketingPartnersCoursesSearchResponse>(url, requestOptions);

export function useClientApiV1MarketingPartnersCoursesSearchQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1MarketingPartnersCoursesSearchResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useQuery<ClientApiV1MarketingPartnersCoursesSearchResponse, unknown>(
    queryKey,
    () => fetchClientApiV1MarketingPartnersCoursesSearch(url, requestOptions),
    options,
  );

  return { ...result, data: result.data?.data };
}

/** 파트너스 강의 검색 */
export type ClientApiV1MarketingPartnersCoursesSearchQueryString = Partial<{
  pageNumber: number;
  pageSize: number;
  keyword: string;
  category: string;
  sort: 'POPULAR' | 'RECENT' | 'PRICE' | 'REVENUE' | 'MARGIN' | 'REVIEW' | 'STUDENT' | 'RATING';
  userId: number;
}>;

export type ClientApiV1MarketingPartnersCoursesSearchResponse = SearchPartnerCourseResponse;

export function useClientApiV1MyListsCourseSequenceCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MyListsCourseSequenceCreateResponse,
    unknown,
    ClientApiV1MyListsCourseSequenceCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MyListsCourseSequenceCreateResponse,
    unknown,
    ClientApiV1MyListsCourseSequenceCreateVariables,
    TContext
  >((variables) => fetcher.post(url, variables, requestOptions), options);

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MyListsCourseSequenceCreateVariables = UpdateMyListCourseSequenceRequest;

export type ClientApiV1MyListsCourseSequenceCreateResponse = EmptyResponse;

export const fetchClientApiV1RoadmapAuthorUserId = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1RoadmapAuthorUserIdResponse>(url, requestOptions);

export function useClientApiV1RoadmapAuthorUserIdQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1RoadmapAuthorUserIdResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useQuery<ClientApiV1RoadmapAuthorUserIdResponse, unknown>(
    queryKey,
    () => fetchClientApiV1RoadmapAuthorUserId(url, requestOptions),
    options,
  );

  return { ...result, data: result.data?.data };
}

/** 주어진 유저가 생성한 로드맵을 조회한다 */
export type ClientApiV1RoadmapAuthorUserIdQueryString = Partial<{
  pageNumber: number;
  pageSize: number;
}>;

export type ClientApiV1RoadmapAuthorUserIdResponse = RoadmapsByAuthorResponse;

export function useClientApiV1VouchersEnrollmentVerificationsConfirmUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1VouchersEnrollmentVerificationsConfirmUpdateResponse,
    unknown,
    ClientApiV1VouchersEnrollmentVerificationsConfirmUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1VouchersEnrollmentVerificationsConfirmUpdateResponse,
    unknown,
    ClientApiV1VouchersEnrollmentVerificationsConfirmUpdateVariables,
    TContext
  >((variables) => fetcher.put(url, variables, requestOptions), options);

  return { ...result, data: result.data?.data };
}

export type ClientApiV1VouchersEnrollmentVerificationsConfirmUpdateVariables =
  ConfirmEnrollmentVerificationRequest;

export type ClientApiV1VouchersEnrollmentVerificationsConfirmUpdateResponse = EmptyResponse;

export function useClientApiV1VouchersIdFreeDeleteMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1VouchersIdFreeDeleteResponse,
    unknown,
    ClientApiV1VouchersIdFreeDeleteVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1VouchersIdFreeDeleteResponse,
    unknown,
    ClientApiV1VouchersIdFreeDeleteVariables,
    TContext
  >((variables) => fetcher.delete(url, variables, requestOptions), options);

  return { ...result, data: result.data?.data };
}

export type ClientApiV1VouchersIdFreeDeleteResponse = EmptyResponse;

export type ClientApiV1VouchersIdFreeDeleteVariables = unknown;

export interface ConfirmCourseCertificateResponse {
  /**
   * 응답데이터
   */
  data: {
    /**
     * UTC 기준 갱신 시각
     */
    nowUTC: string;
    /**
     * 갱신 시각
     */
    now: string;
  };
  /**
   * 에러코드
   */
  errorCode?: string | null;
  /**
   * 메시지
   */
  message: string;
  /**
   * 상태코드
   */
  statusCode: string;
}

export interface CourseSimpleOneResponse {
  /**
   * 응답데이터
   */
  data: {
    /**
     * 챌린지 강의 정보
     */
    challengeCourse?: {
      /**
       * 원본 강의 ID
       */
      originalCourseId?: number | null;
    };
    /**
     * 강의 수강 기한(단위: 월)
     */
    duration: number;
    /**
     * 게시일
     */
    publishedAtUTC?: string | null;
    /**
     * 수강생 수
     */
    studentCount: number;
    /**
     * 제출일
     */
    submittedAtUTC?: string | null;
    /**
     * 정가
     */
    regularPrice?: number | null;
    /**
     * 강의 아이디
     */
    id: number;
    type: 'ON' | 'OFF' | 'CHALLENGE';
    /**
     * 강의 제목
     */
    title: string;
    /**
     * 강의 슬러그
     */
    slug?: string | null;
    status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  };
  /**
   * 에러코드
   */
  errorCode?: string | null;
  /**
   * 메시지
   */
  message: string;
  /**
   * 상태코드
   */
  statusCode: string;
}

export interface GetEnrollmentVerificationResponse {
  /**
   * 응답데이터
   */
  data: {
    /**
     * 강의명
     */
    courseTitle: string;
    /**
     * 강의 슬러그
     */
    courseSlug: string;
    /**
     * 수강권 생성(수강 시작) 일시
     */
    createdAt: string;
    /**
     * 강의 썸네일 URL
     */
    courseThumbnailUrl: string;
    /**
     * UTC 기준 수강권 생성(수강 시작) 일시
     */
    createdAtUTC: string;
    /**
     * 수강확인 여부
     */
    isConfirmed: boolean;
    /**
     * 수강권 ID
     */
    id: number;
    /**
     * 강의 ID
     */
    courseId: number;
  }[];
  /**
   * 에러코드
   */
  errorCode?: string | null;
  /**
   * 메시지
   */
  message: string;
  /**
   * 상태코드
   */
  statusCode: string;
}

export interface FindOrderSummaryResponse {
  /**
   * 응답데이터
   */
  data: {
    items?: {
      /**
       * pgData가 없어 영수증을 조회할 수 없는 과거 데이터 여부
       */
      isOldDataWithNoReceipt: boolean;
      /**
       * 타임존
       */
      timezone: string;
      /**
       * 정가금액
       */
      regularPrice: number;
      /**
       * 강의 제목
       */
      title: string;
      /**
       * 주문서 개별 항목 개수
       */
      itemsCount: number;
      /**
       * 결제 채널
       */
      paymentChannel: string;
      /**
       * 결제금액
       */
      paymentPrice: number;
      /**
       * 영수증 조회 가능 여부
       */
      isReceiptAvailable: boolean;
      /**
       * 주문 생성 일시
       */
      createdAt: string;
      /**
       * 거래명세서 조회 가능 여부
       */
      canGetInvoice: boolean;
      /**
       * 영수증 조회 가능 여부
       */
      canGetReceipt: boolean;
      orderMode:
        | 'NORMAL'
        | 'B2B_CODE'
        | 'B2C_CODE'
        | 'CODE'
        | 'MENTOR'
        | 'MENTORING'
        | 'COURSE_GIFT'
        | 'CLIP';
      /**
       * UTC 기준 주문 생성 일시
       */
      createdAtUTC: string;
      /**
       * 통화
       */
      currency: string;
      /**
       * 주문서 아이디
       */
      id: number;
      /**
       * 영수증 URL
       */
      receiptUrl: string;
      status:
        | 'CANCEL_DELETE'
        | 'MENTEE_CANCEL'
        | 'PARTIAL_CANCEL'
        | 'CANCEL_NOT_ENOUGH_PROGRESS'
        | 'GROUP_PAYMENT_CANCEL'
        | 'PAYMENT_CANCEL'
        | 'ORDER_FORM'
        | 'CONFLICT_CANCEL'
        | 'AUTO_CANCEL'
        | 'PAYMENT_READY'
        | 'TOTAL_CANCEL'
        | 'PAYMENT_FAIL'
        | 'PAYMENT_STOP'
        | 'CODE_ORDER'
        | 'ORDER_CANCEL'
        | 'PAYMENT_COMPLETE'
        | 'PAYMENT_COMPLETE_PROCESSING'
        | 'ADMIN_CANCEL'
        | 'MENTOR_CANCEL'
        | 'DEPOSIT_WAIT'
        | 'WC_COMPLETED'
        | 'MANUAL_DELETE'
        | 'REFUND_REQUEST';
    }[];
    statistics?: {
      /**
       * 총 주문금액
       */
      totalOrderAmount: number;
      /**
       * 총 할인금액
       */
      totalDiscountAmount: number;
      /**
       * 총 결제금액
       */
      totalPaymentAmount: number;
      /**
       * 구매강의 수
       */
      totalCount: number;
    };
  };
  /**
   * 에러코드
   */
  errorCode?: string | null;
  /**
   * 메시지
   */
  message: string;
  /**
   * 상태코드
   */
  statusCode: string;
}

export interface UnconfirmedResponse {
  /**
   * 응답데이터
   */
  data: {
    /**
     * 미확인 수료증 존재 여부
     */
    hasUnconfirmedCertificate: boolean;
  };
  /**
   * 에러코드
   */
  errorCode?: string | null;
  /**
   * 메시지
   */
  message: string;
  /**
   * 상태코드
   */
  statusCode: string;
}

export interface RemoveCartsRequest {
  /**
   * 장바구니에서 제거할 강의 ID 목록
   */
  courseIds: number[];
}

export interface FindMyListByCourseResponse {
  /**
   * 응답데이터
   */
  data: {
    /**
     * 강의폴더 리스트
     */
    items: {
      /**
       * 해당 강의 유무
       */
      hasCourse: boolean;
      /**
       * id
       */
      id: number;
      /**
       * 제목
       */
      title: string;
    }[];
  };
  /**
   * 에러코드
   */
  errorCode?: string | null;
  /**
   * 메시지
   */
  message: string;
  /**
   * 상태코드
   */
  statusCode: string;
}

export interface EmptyResponse {
  data?: object;
  /**
   * 에러코드
   */
  errorCode?: string | null;
  /**
   * 메시지
   */
  message: string;
  /**
   * 상태코드
   */
  statusCode: string;
}

export interface ConfirmCourseCertificateRequestV2 {
  /**
   * 수강권 ID
   */
  voucherId: number;
}

export interface FindReceivedCourseGiftResponse {
  /**
   * 응답데이터
   */
  data: {
    /**
     * 페이지 번호
     */
    pageNumber: number;
    /**
     * 총 페이지 수
     */
    totalPage: number;
    /**
     * 페이지 당 노출 갯수
     */
    pageSize: number;
    /**
     * 전체 갯수
     */
    totalCount: number;
    /**
     * 받은 선물 목록
     */
    items: {
      /**
       * 선물 카드 이미지 url
       */
      giftPhotoCardImageUrl: string;
      /**
       * UTC 기준 수락 일시
       */
      approvedAtUTC: string;
      /**
       * 강의 삭제 여부
       */
      isDeletedCourse: boolean;
      /**
       * 취소 여부
       */
      isCanceled: boolean;
      /**
       * 선물 코드
       */
      code: string;
      /**
       * 선물 카드 이미지 id
       */
      giftPhotoCardId: number;
      /**
       * 수락 일시
       */
      approvedAt: string;
      /**
       * 수강 만료 일시
       */
      limitedAt?: string | null;
      /**
       * 수강 기한 개월 수
       */
      duration?: number | null;
      /**
       * 강의 슬러그
       */
      courseSlug: string;
      /**
       * 강의명
       */
      courseTitle: string;
      /**
       * UTC 기준 취소 일시
       */
      canceledAtUTC?: string | null;
      /**
       * 주문 생성 일시
       */
      createdAt: string;
      /**
       * 발신자 이름
       */
      senderName: string;
      /**
       * 취소 일시
       */
      canceledAt?: string | null;
      /**
       * UTC 기준 수강 만료 일시
       */
      limitedAtUTC?: string | null;
      /**
       * UTC 기준 주문 생성 일시
       */
      createdAtUTC: string;
      /**
       * 선물 ID
       */
      id: number;
      /**
       * 주문명
       */
      orderTitle: string;
      status: 'RECEIVED' | 'CANCELED';
    }[];
  };
  /**
   * 에러코드
   */
  errorCode?: string | null;
  /**
   * 메시지
   */
  message: string;
  /**
   * 상태코드
   */
  statusCode: string;
}

export interface UserStatisticsResponse {
  /**
   * 응답데이터
   */
  data: {
    /**
     * 결제한 강의 아이디
     */
    userCourses: number[];
    /**
     * 결제한 유료강의의 총 결제금액
     */
    userCourseTotalPaymentPrice: number;
    /**
     * 결제한 유료강의수
     */
    userCourseCnt: number;
    /**
     * 내 카테고리 정보
     */
    preferCategories: string[];
  };
  /**
   * 에러코드
   */
  errorCode?: string | null;
  /**
   * 메시지
   */
  message: string;
  /**
   * 상태코드
   */
  statusCode: string;
}

export interface SignupMarketingPartnerResponse {
  /**
   * 응답데이터
   */
  data: {
    /**
     * 정산율
     */
    commissionRate: {
      /**
       * 마케팅 비동의 강의에 대한 정산율
       */
      disagreed: number;
      /**
       * 마케팅 동의 강의에 대한 정산율
       */
      agreed: number;
    };
    /**
     * 마케팅 파트너 ID
     */
    id: number;
  };
  /**
   * 에러코드
   */
  errorCode?: string | null;
  /**
   * 메시지
   */
  message: string;
  /**
   * 상태코드
   */
  statusCode: string;
}

export interface CreateMyListRequest {
  /**
   * 강의폴더 제목
   */
  title: string;
}

export interface UserProfileCourseResponse {
  /**
   * 응답데이터
   */
  data: {
    /**
     * 현제 페이지
     */
    pageNumber: number;
    /**
     * 전체 페이지
     */
    totalPage: number;
    /**
     * 페이지 요청 크기
     */
    pageSize: number;
    /**
     * 전체 개수
     */
    totalCount: number;
    items?: {
      /**
       * 챌린지 강의 정보
       */
      challengeCourse?: {
        /**
         * UTC 기준 신청 종료일
         */
        applyEndedAtUTC: string;
        /**
         * 신청 인원
         */
        applicantCount: number;
        /**
         * UTC 기준 행사 종료일
         */
        eventEndedAtUTC: string;
        /**
         * UTC 기준 신청 시작일
         */
        applyStartedAtUTC: string;
        /**
         * 챌린지 원본 강의 수강생 수
         */
        originalCourseStudentCount: number;
        /**
         * UTC 기준 행사 시작일
         */
        eventStartedAtUTC: string;
        /**
         * 설문 응답 가능 여부
         */
        canAnswerSurvey: boolean;
        /**
         * 설문 응답 여부
         */
        hasSurveyAnswer: boolean;
        /**
         * 수용 인원
         */
        capacity: number;
      };
      /**
       * 수강권 정보
       */
      voucher?: {
        /**
         * 유닛 수
         */
        lectureCount: number;
        /**
         * 진도율
         */
        progress: string;
        /**
         * 완료 수
         */
        completeCount: number;
      };
      /**
       * 멘토링 포함 여부
       */
      hasMentoring: boolean;
      /**
       * KRW 결제 가격
       */
      krwPaymentPrice: number;
      /**
       * KRW 정가
       */
      krwRegularPrice: number;
      /**
       * KDT 여부
       */
      isKdt: boolean;
      type: 'ON' | 'OFF' | 'CHALLENGE';
      /**
       * 장바구니 정보
       */
      cart: {
        /**
         * 장바구니 사용 가능 여부
         */
        canUseCart: boolean;
        /**
         * 장바구니에 담겨있는지 여부
         */
        isInCart: boolean;
      };
      /**
       * 강사 정보
       */
      instructor: {
        /**
         * 강사 이름
         */
        name: string;
        /**
         * 강사 아이디
         */
        id: number;
      };
      /**
       * 좋아요 여부
       */
      hasMyReaction: boolean;
      /**
       * 가격 숨김 여부
       */
      isHidePrice: boolean;
      /**
       * 강의 정보
       */
      course: {
        /**
         * UTC 기준 오픈 일시
         */
        publishedAtUTC: string;
        /**
         * 수강생 수
         */
        studentCount: number;
        /**
         * 메타데이터
         */
        metadata: {
          /**
           * 스킬
           */
          skills: string[];
          levelCode: 'Beginner' | 'Basic' | 'Intermediate' | 'CodeNotExists';
          /**
           * 스킬 태그
           */
          skillTags: {
            /**
             * 스킬 태그 아이디
             */
            id: number;
            /**
             * 스킬 태그 이름
             */
            title: string;
            /**
             * 스킬 태그 슬러그
             */
            slug: string;
          }[];
          /**
           * 레벨
           */
          level: string;
          /**
           * 소 카테고리
           */
          childCategories: string[];
          /**
           * 카테고리
           */
          categories: {
            /**
             * 대 카테고리
             */
            parent: {
              /**
               * 대 카테고리 아이디
               */
              id: number;
              /**
               * 대 카테고리 이름
               */
              title: string;
              /**
               * 대 카테고리 슬러그
               */
              slug: string;
            };
            /**
             * 카테고리 아이디
             */
            id: number;
            /**
             * 카테고리 이름
             */
            title: string;
            /**
             * 카테고리 슬러그
             */
            slug: string;
          }[];
          /**
           * 대 카테고리
           */
          parentCategories: string[];
        };
        /**
         * 강의 태그
         */
        courseTags: {
          tag:
            | 'IS_ACTIVE_IN_QUESTION'
            | 'IS_B2B_SUBSCRIPTION'
            | 'HAS_MISSION'
            | 'IS_CHALLENGE_APPLYING'
            | 'IS_CHALLENGE_BEFORE_APPLY'
            | 'IS_CHALLENGE_AFTER_APPLY'
            | 'IS_CHALLENGE_PROCESSING'
            | 'IS_CHALLENGE_COMPLETED'
            | 'IS_CHALLENGE_PARTICIPATING';
        }[];
        /**
         * 별점
         */
        star: number;
        /**
         * 오픈 일시
         */
        publishedAt: string;
        /**
         * 업데이트 여부
         */
        isUpdated: boolean;
        /**
         * 강의 설명
         */
        description: string;
        /**
         * 좋아요 수
         */
        likeCount: number;
        /**
         * 신규 여부
         */
        isNew: boolean;
        /**
         * 강의 제목
         */
        title: string;
        /**
         * 대상
         */
        targets: string[];
        /**
         * 강의 런타임
         */
        runtimeSecond: number;
        /**
         * 배우는 것
         */
        abilities: string[];
        /**
         * 독점 여부
         */
        isExclusive: boolean;
        /**
         * 리뷰 수
         */
        reviewCount: number;
        /**
         * 강의 아이디
         */
        id: number;
        /**
         * 강의 슬러그
         */
        slug: string;
        /**
         * UTC 기준 업데이트 일시
         */
        updatedAtUTC: string;
        /**
         * 업데이트 일시
         */
        updatedAt: string;
        /**
         * 썸네일 이미지 URL
         */
        thumbnailUrl: string;
      };
      /**
       * 강의 아이디
       */
      id: number;
      /**
       * 오프라인 강의 정보
       */
      offlineCourse?: {
        /**
         * UTC 기준 신청 시작일
         */
        applyStartedAtUTC: string;
        /**
         * 설문 응답 가능 여부
         */
        canAnswerSurvey: boolean;
        /**
         * 설문 응답 여부
         */
        hasSurveyAnswer: boolean;
        type: 'CONFERENCE' | 'CLASS';
        /**
         * 행사 시작일
         */
        eventStartedAt: string;
        /**
         * 행사 종료일
         */
        eventEndedAt: string;
        /**
         * 수용 인원
         */
        capacity: number;
        /**
         * UTC 기준 신청 종료일
         */
        applyEndedAtUTC: string;
        /**
         * 신청 인원
         */
        applicantCount: number;
        /**
         * UTC 기준 행사 종료일
         */
        eventEndedAtUTC: string;
        /**
         * 신청 시작일
         */
        applyStartedAt: string;
        /**
         * UTC 기준 행사 시작일
         */
        eventStartedAtUTC: string;
        /**
         * 신청 종료일
         */
        applyEndedAt: string;
      };
      /**
       * (프로모션카드) tagTitle 리스트
       */
      tagTitles: string[];
      /**
       * 가격 정보
       */
      listPrice: {
        /**
         * UTC 기준 할인 종료일
         */
        discountEndedAtUTC?: string | null;
        /**
         * 할인율
         */
        discountRate: number;
        /**
         * 할인 제목
         */
        discountTitle?: string | null;
        /**
         * 무료 여부
         */
        isFree: boolean;
        /**
         * 할인 종료일
         */
        discountEndedAt?: string | null;
        /**
         * 결제할 가격
         */
        payPrice: number;
        /**
         * 정가
         */
        regularPrice: number;
        /**
         * 할인 여부
         */
        isDiscount: boolean;
        /**
         * 할인 아이디
         */
        discountId?: number | null;
      };
    }[];
  };
  /**
   * 에러코드
   */
  errorCode?: string | null;
  /**
   * 메시지
   */
  message: string;
  /**
   * 상태코드
   */
  statusCode: string;
}

export interface RemoveCourseRequest {
  /**
   * 강의를 제거할 강의폴더 id
   */
  listId: number;
  /**
   * 강의폴더에서 제거할 강의 id
   */
  courseId: number;
}

export interface GetCertificatesResponse {
  /**
   * 응답데이터
   */
  data: {
    /**
     * 강의 제목
     */
    courseTitle: string;
    /**
     * 강의 슬러그
     */
    courseSlug: string;
    /**
     * UTC 기준 수료 시각
     */
    certificatedAtUTC?: string | null;
    /**
     * 강의 썸네일 URL
     */
    courseThumbnailUrl: string;
    courseType: 'ON' | 'OFF' | 'CHALLENGE';
    /**
     * 수료 시각
     */
    certificatedAt?: string | null;
    /**
     * 수료증 확인 시각
     */
    certificateConfirmedAt?: string | null;
    /**
     * 수강권 ID
     */
    voucherId: number;
    /**
     * UTC 기준 수료증 확인 시각
     */
    certificateConfirmedAtUTC?: string | null;
    /**
     * 강의 ID
     */
    courseId: number;
    /**
     * 사용자 ID
     */
    userId: number;
  }[];
  /**
   * 에러코드
   */
  errorCode?: string | null;
  /**
   * 메시지
   */
  message: string;
  /**
   * 상태코드
   */
  statusCode: string;
}

export interface AddCourseRequest {
  /**
   * 강의를 추가할 강의폴더 id
   */
  listId: number;
  /**
   * 강의폴더에 추가할 강의 id
   */
  courseId: number;
}

export interface ConfirmEnrollmentVerificationRequest {
  /**
   * 수강확인증 ID
   */
  voucherId: number;
}

export interface UpdateMyListsSequenceRequest {
  /**
   * 순서를 수정할 강의폴더 id를 순서대로 배열한 리스트
   */
  ids: number[];
}

export interface ArchiveVoucherV2Request {
  /**
   * 숨김/취소 여부
   */
  isArchive: boolean;
  /**
   * 수강권 ID
   */
  id: number;
}

export interface SignupMarketingPartnerRequest {
  /**
   * 휴대폰 번호
   */
  phone: string;
  /**
   * 휴대폰 번호 국가 코드
   */
  countryCode: string;
  /**
   * 활동 링크
   */
  activityLink?: string | null;
  /**
   * 사업체명 또는 계약자명
   */
  name: string;
  type: 'BUSINESS' | 'PERSONAL';
  /**
   * 이메일
   */
  email: string;
}

export interface FindSentCourseGiftResponse {
  /**
   * 응답데이터
   */
  data: {
    /**
     * 페이지 번호
     */
    pageNumber: number;
    /**
     * 총 페이지 수
     */
    totalPage: number;
    /**
     * 페이지 당 노출 갯수
     */
    pageSize: number;
    /**
     * 전체 갯수
     */
    totalCount: number;
    /**
     * 보낸 선물 목록
     */
    items: {
      /**
       * 강의 삭제 여부
       */
      isDeletedCourse: boolean;
      /**
       * 타임존
       */
      timezone: string;
      /**
       * UTC 기준 환불 만료 일시
       */
      refundLimitedAtUTC: string;
      /**
       * 수신자 이름
       */
      receiverName: string;
      /**
       * 정가금액
       */
      regularPrice: number;
      /**
       * 거래명세서 조회 가능 여부
       */
      isInvoiceAvailable: boolean;
      /**
       * 결제 채널
       */
      paymentChannel: string;
      /**
       * 결제금액
       */
      paymentPrice: number;
      /**
       * 환불 가능 여부
       */
      isRefundable: boolean;
      /**
       * 강의 슬러그
       */
      courseSlug: string;
      /**
       * 강의명
       */
      courseTitle: string;
      /**
       * 영수증 조회 가능 여부
       */
      isReceiptAvailable: boolean;
      /**
       * 주문 생성 일시
       */
      createdAt: string;
      /**
       * 환불 만료 일시
       */
      refundLimitedAt: string;
      /**
       * UTC 기준 주문 생성 일시
       */
      createdAtUTC: string;
      /**
       * 통화
       */
      currency: string;
      /**
       * 선물 ID
       */
      id: number;
      /**
       * 주문명
       */
      orderTitle: string;
      /**
       * 영수증 URL
       */
      receiptUrl: string;
      status: 'READY_TO_RECEIVE' | 'RECEIVED' | 'EXPIRED' | 'CANCELED';
    }[];
  };
  /**
   * 에러코드
   */
  errorCode?: string | null;
  /**
   * 메시지
   */
  message: string;
  /**
   * 상태코드
   */
  statusCode: string;
}

export interface CreateMarketingPartnerLinkRequest {
  /**
   * 강의 ID
   */
  courseId: number;
}

export interface SearchPartnerCourseResponse {
  /**
   * 응답데이터
   */
  data: {
    /**
     * 현재 페이지
     */
    pageNumber: number;
    /**
     * 마케팅 파트너 정보
     */
    partner: {
      /**
       * 마케팅 파트너 수익률
       */
      commissionRate: {
        /**
         * 마케팅 비동의 강의에 대한 수익률
         */
        disagreed: number;
        /**
         * 마케팅 동의 강의에 대한 수익률
         */
        agreed: number;
      };
      /**
       * 마케팅 파트너 아이디
       */
      id: number;
      /**
       * 정산 정보 입력 여부
       */
      hasSettlementInfo: boolean;
      type: 'BUSINESS' | 'PERSONAL';
    };
    /**
     * 전체 페이지
     */
    totalPage: number;
    /**
     * 페이지 요청 크기
     */
    pageSize: number;
    /**
     * 전체 개수
     */
    totalCount: number;
    /**
     * 강의 목록
     */
    items: {
      /**
       * 예상 수익
       */
      expectedRevenue: number;
      /**
       * 해당 강의에 대한 수익률
       */
      commissionRate: number;
      /**
       * 강사 정보
       */
      instructor: {
        /**
         * 강사 이름
         */
        name: string;
        /**
         * 강사 아이디
         */
        id: number;
      };
      /**
       * 강의 정보
       */
      course: {
        /**
         * UTC 기준 오픈 일시
         */
        publishedAtUTC: string;
        /**
         * 수강생 수
         */
        studentCount: number;
        /**
         * 메타데이터
         */
        metadata: {
          /**
           * 스킬
           */
          skills: string[];
          levelCode: 'Beginner' | 'Basic' | 'Intermediate' | 'CodeNotExists';
          /**
           * 스킬 태그
           */
          skillTags: {
            /**
             * 스킬 태그 아이디
             */
            id: number;
            /**
             * 스킬 태그 이름
             */
            title: string;
            /**
             * 스킬 태그 슬러그
             */
            slug: string;
          }[];
          /**
           * 레벨
           */
          level: string;
          /**
           * 카테고리
           */
          categories: {
            /**
             * 대 카테고리
             */
            parent: {
              /**
               * 대 카테고리 아이디
               */
              id: number;
              /**
               * 대 카테고리 이름
               */
              title: string;
              /**
               * 대 카테고리 슬러그
               */
              slug: string;
            };
            /**
             * 카테고리 아이디
             */
            id: number;
            /**
             * 카테고리 이름
             */
            title: string;
            /**
             * 카테고리 슬러그
             */
            slug: string;
          }[];
        };
        /**
         * 별점
         */
        star: number;
        /**
         * 오픈 일시
         */
        publishedAt: string;
        /**
         * 강의 설명
         */
        description: string;
        /**
         * 좋아요 수
         */
        likeCount: number;
        /**
         * 강의 제목
         */
        title: string;
        /**
         * 대상
         */
        targets: string[];
        /**
         * 강의 런타임
         */
        runtimeSecond: number;
        /**
         * 배우는 것
         */
        abilities: string[];
        /**
         * 리뷰 수
         */
        reviewCount: number;
        /**
         * 강의 아이디
         */
        id: number;
        /**
         * 강의 슬러그
         */
        slug: string;
        /**
         * UTC 기준 업데이트 일시
         */
        updatedAtUTC: string;
        /**
         * 업데이트 일시
         */
        updatedAt: string;
        /**
         * 썸네일 이미지 URL
         */
        thumbnailUrl: string;
      };
      /**
       * 가격 정보
       */
      listPrice: {
        /**
         * UTC 기준 할인 종료일
         */
        discountEndedAtUTC?: string | null;
        /**
         * 할인율
         */
        discountRate: number;
        /**
         * 할인 제목
         */
        discountTitle?: string | null;
        /**
         * 원가
         */
        originalPrice: number;
        /**
         * 할인 종료일
         */
        discountEndedAt?: string | null;
        /**
         * 결제할 가격
         */
        payPrice: number;
        /**
         * 정가
         */
        regularPrice: number;
        /**
         * 할인 여부
         */
        isDiscount: boolean;
        /**
         * 할인 아이디
         */
        discountId?: number | null;
      };
    }[];
  };
  /**
   * 에러코드
   */
  errorCode?: string | null;
  /**
   * 메시지
   */
  message: string;
  /**
   * 상태코드
   */
  statusCode: string;
}

export interface UpdateMyListCourseSequenceRequest {
  /**
   * 강의 순서를 수정할 강의폴더 id
   */
  listId: number;
  /**
   * 수정할 순서대로 배열된 강의 id 리스트
   */
  courseIds: number[];
}

export interface MyVoucherDto {
  /**
   * 응답데이터
   */
  data: {
    /**
     * 현제 페이지
     */
    pageNumber: number;
    /**
     * 전체 페이지
     */
    totalPage: number;
    /**
     * 페이지 요청 크기
     */
    pageSize: number;
    /**
     * 전체 개수
     */
    totalCount: number;
    items?: {
      cancelType?: 'B2B' | 'B2C';
      /**
       * 숨김 처리 여부
       */
      isArchived: boolean;
      /**
       * 수료증 발급 여부
       */
      hasCertificate: boolean;
      /**
       * 숨김 처리 가능 여부
       */
      isArchivable: boolean;
      type:
        | 'LEGACY'
        | 'IS_ADMIN'
        | 'BY_ADMIN'
        | 'IS_INSTRUCTOR'
        | 'BY_INSTRUCTOR'
        | 'FREE'
        | 'ORDER'
        | 'CODE'
        | 'GROUP_ORDER'
        | 'GROUP_SUBSCRIPTION'
        | 'COURSE_GIFT';
      /**
       * 완료 수업 수
       */
      completeCount: number;
      /**
       * 만료일시
       */
      limitedAt?: string | null;
      /**
       * 유저 ID
       */
      userId: number;
      /**
       * 리뷰 작성 가능 여부
       */
      isReviewable: boolean;
      /**
       * UTC 기준 만료일시
       */
      limitedAtUTC?: string | null;
      /**
       * 수강평 정보
       */
      review?: {
        /**
         * 수강평 1점 사유
         */
        reasons: string[];
        /**
         * 수강평 점수
         */
        rating: number;
        /**
         * 수강평 id
         */
        id: number;
        /**
         * 수강평 내용
         */
        content: string;
      };
      /**
       * 강의 정보
       */
      course: {
        /**
         * 챌린지 강의 정보
         */
        challengeCourse?: {
          /**
           * UTC 기준 챌린지 강의 시작일시
           */
          startedAtUTC: string;
          /**
           * 챌린지 강의 id
           */
          id: number;
          /**
           * UTC 기준 챌린지 강의 종료일시
           */
          endedAtUTC: string;
        };
        /**
         * 강사명
         */
        instructorName: string;
        /**
         * 강의 수업 수
         */
        lectureCount: number;
        /**
         * 소카테고리 정보
         */
        childCategories: string[];
        /**
         * 강의 id
         */
        id: number;
        /**
         * 오프라인 강의 정보
         */
        offlineCourse?: {
          /**
           * 오프라인 강의 종료일시
           */
          endedAt: string;
          /**
           * UTC 기준 오프라인 강의 시작일시
           */
          startedAtUTC: string;
          /**
           * 오프라인 강의 시작일시
           */
          startedAt: string;
          /**
           * 오프라인 강의 설문 응답 가능 여부
           */
          canAnswerSurvey: boolean;
          /**
           * 오프라인 강의 id
           */
          id: number;
          /**
           * 오프라인 강의 설문 응답 여부
           */
          hasSurveyAnswer: boolean;
          /**
           * UTC 기준 오프라인 강의 종료일시
           */
          endedAtUTC: string;
        };
        /**
         * 강의명
         */
        title: string;
        /**
         * 강의 슬러그
         */
        slug: string;
        /**
         * 강사 ID
         */
        instructorId: number;
        /**
         * 강의 썸네일 url
         */
        thumbnailUrl: string;
      };
      /**
       * 진도율
       */
      progress: string;
      /**
       * 최근 수강 유닛 ID
       */
      unitId: number;
      /**
       * 수강권 ID
       */
      id: number;
    }[];
  };
  /**
   * 에러코드
   */
  errorCode?: string | null;
  /**
   * 메시지
   */
  message: string;
  /**
   * 상태코드
   */
  statusCode: string;
}

export interface FindParentCategoryResponse {
  /**
   * 응답데이터
   */
  data: {
    /**
     * 카테고리 목록
     */
    categories: {
      /**
       * 원본 제목
       */
      originalTitle: string;
      /**
       * 아이콘
       */
      icon: string;
      /**
       * 아이디
       */
      id: number;
      /**
       * 제목
       */
      title: string;
      /**
       * 슬러그
       */
      slug: string;
    }[];
  };
  /**
   * 에러코드
   */
  errorCode?: string | null;
  /**
   * 메시지
   */
  message: string;
  /**
   * 상태코드
   */
  statusCode: string;
}

export interface CreateMarketingPartnerLinkResponse {
  /**
   * 응답데이터
   */
  data: {
    /**
     * 생성된 홍보링크 URL
     */
    link: string;
  };
  /**
   * 에러코드
   */
  errorCode?: string | null;
  /**
   * 메시지
   */
  message: string;
  /**
   * 상태코드
   */
  statusCode: string;
}

export interface GetMarketingPartnerLinkCreatePreviewResponse {
  /**
   * 응답데이터
   */
  data: {
    /**
     * 홍보링크 수익률
     */
    commissionRate: number;
    /**
     * 홍보링크 수익 인정 기간
     */
    attributeWindowDays: number;
    /**
     * 홍보링크 생성 가능 여부
     */
    canCreate: boolean;
  };
  /**
   * 에러코드
   */
  errorCode?: string | null;
  /**
   * 메시지
   */
  message: string;
  /**
   * 상태코드
   */
  statusCode: string;
}

export interface CourseByLikesResponse {
  /**
   * 응답데이터
   */
  data: {
    items?: {
      /**
       * 챌린지 강의 정보
       */
      challengeCourse?: {
        /**
         * UTC 기준 신청 종료일
         */
        applyEndedAtUTC: string;
        /**
         * 신청 인원
         */
        applicantCount: number;
        /**
         * UTC 기준 행사 종료일
         */
        eventEndedAtUTC: string;
        /**
         * UTC 기준 신청 시작일
         */
        applyStartedAtUTC: string;
        /**
         * 챌린지 원본 강의 수강생 수
         */
        originalCourseStudentCount: number;
        /**
         * UTC 기준 행사 시작일
         */
        eventStartedAtUTC: string;
        /**
         * 설문 응답 가능 여부
         */
        canAnswerSurvey: boolean;
        /**
         * 설문 응답 여부
         */
        hasSurveyAnswer: boolean;
        /**
         * 수용 인원
         */
        capacity: number;
      };
      /**
       * 수강권 정보
       */
      voucher?: {
        /**
         * 유닛 수
         */
        lectureCount: number;
        /**
         * 진도율
         */
        progress: string;
        /**
         * 완료 수
         */
        completeCount: number;
      };
      /**
       * 멘토링 포함 여부
       */
      hasMentoring: boolean;
      /**
       * KRW 결제 가격
       */
      krwPaymentPrice: number;
      /**
       * KRW 정가
       */
      krwRegularPrice: number;
      /**
       * KDT 여부
       */
      isKdt: boolean;
      type: 'ON' | 'OFF' | 'CHALLENGE';
      /**
       * 장바구니 정보
       */
      cart: {
        /**
         * 장바구니 사용 가능 여부
         */
        canUseCart: boolean;
        /**
         * 장바구니에 담겨있는지 여부
         */
        isInCart: boolean;
      };
      /**
       * 강사 정보
       */
      instructor: {
        /**
         * 강사 이름
         */
        name: string;
        /**
         * 강사 아이디
         */
        id: number;
      };
      /**
       * 좋아요 여부
       */
      hasMyReaction: boolean;
      /**
       * 가격 숨김 여부
       */
      isHidePrice: boolean;
      /**
       * 강의 정보
       */
      course: {
        /**
         * UTC 기준 오픈 일시
         */
        publishedAtUTC: string;
        /**
         * 수강생 수
         */
        studentCount: number;
        /**
         * 메타데이터
         */
        metadata: {
          /**
           * 스킬
           */
          skills: string[];
          levelCode: 'Beginner' | 'Basic' | 'Intermediate' | 'CodeNotExists';
          /**
           * 스킬 태그
           */
          skillTags: {
            /**
             * 스킬 태그 아이디
             */
            id: number;
            /**
             * 스킬 태그 이름
             */
            title: string;
            /**
             * 스킬 태그 슬러그
             */
            slug: string;
          }[];
          /**
           * 레벨
           */
          level: string;
          /**
           * 소 카테고리
           */
          childCategories: string[];
          /**
           * 카테고리
           */
          categories: {
            /**
             * 대 카테고리
             */
            parent: {
              /**
               * 대 카테고리 아이디
               */
              id: number;
              /**
               * 대 카테고리 이름
               */
              title: string;
              /**
               * 대 카테고리 슬러그
               */
              slug: string;
            };
            /**
             * 카테고리 아이디
             */
            id: number;
            /**
             * 카테고리 이름
             */
            title: string;
            /**
             * 카테고리 슬러그
             */
            slug: string;
          }[];
          /**
           * 대 카테고리
           */
          parentCategories: string[];
        };
        /**
         * 강의 태그
         */
        courseTags: {
          tag:
            | 'IS_ACTIVE_IN_QUESTION'
            | 'IS_B2B_SUBSCRIPTION'
            | 'HAS_MISSION'
            | 'IS_CHALLENGE_APPLYING'
            | 'IS_CHALLENGE_BEFORE_APPLY'
            | 'IS_CHALLENGE_AFTER_APPLY'
            | 'IS_CHALLENGE_PROCESSING'
            | 'IS_CHALLENGE_COMPLETED'
            | 'IS_CHALLENGE_PARTICIPATING';
        }[];
        /**
         * 별점
         */
        star: number;
        /**
         * 오픈 일시
         */
        publishedAt: string;
        /**
         * 업데이트 여부
         */
        isUpdated: boolean;
        /**
         * 강의 설명
         */
        description: string;
        /**
         * 좋아요 수
         */
        likeCount: number;
        /**
         * 신규 여부
         */
        isNew: boolean;
        /**
         * 강의 제목
         */
        title: string;
        /**
         * 대상
         */
        targets: string[];
        /**
         * 강의 런타임
         */
        runtimeSecond: number;
        /**
         * 배우는 것
         */
        abilities: string[];
        /**
         * 독점 여부
         */
        isExclusive: boolean;
        /**
         * 리뷰 수
         */
        reviewCount: number;
        /**
         * 강의 아이디
         */
        id: number;
        /**
         * 강의 슬러그
         */
        slug: string;
        /**
         * UTC 기준 업데이트 일시
         */
        updatedAtUTC: string;
        /**
         * 업데이트 일시
         */
        updatedAt: string;
        /**
         * 썸네일 이미지 URL
         */
        thumbnailUrl: string;
      };
      /**
       * 강의 아이디
       */
      id: number;
      /**
       * 오프라인 강의 정보
       */
      offlineCourse?: {
        /**
         * UTC 기준 신청 시작일
         */
        applyStartedAtUTC: string;
        /**
         * 설문 응답 가능 여부
         */
        canAnswerSurvey: boolean;
        /**
         * 설문 응답 여부
         */
        hasSurveyAnswer: boolean;
        type: 'CONFERENCE' | 'CLASS';
        /**
         * 행사 시작일
         */
        eventStartedAt: string;
        /**
         * 행사 종료일
         */
        eventEndedAt: string;
        /**
         * 수용 인원
         */
        capacity: number;
        /**
         * UTC 기준 신청 종료일
         */
        applyEndedAtUTC: string;
        /**
         * 신청 인원
         */
        applicantCount: number;
        /**
         * UTC 기준 행사 종료일
         */
        eventEndedAtUTC: string;
        /**
         * 신청 시작일
         */
        applyStartedAt: string;
        /**
         * UTC 기준 행사 시작일
         */
        eventStartedAtUTC: string;
        /**
         * 신청 종료일
         */
        applyEndedAt: string;
      };
      /**
       * (프로모션카드) tagTitle 리스트
       */
      tagTitles: string[];
      /**
       * 가격 정보
       */
      listPrice: {
        /**
         * UTC 기준 할인 종료일
         */
        discountEndedAtUTC?: string | null;
        /**
         * 할인율
         */
        discountRate: number;
        /**
         * 할인 제목
         */
        discountTitle?: string | null;
        /**
         * 무료 여부
         */
        isFree: boolean;
        /**
         * 할인 종료일
         */
        discountEndedAt?: string | null;
        /**
         * 결제할 가격
         */
        payPrice: number;
        /**
         * 정가
         */
        regularPrice: number;
        /**
         * 할인 여부
         */
        isDiscount: boolean;
        /**
         * 할인 아이디
         */
        discountId?: number | null;
      };
    }[];
  };
  /**
   * 에러코드
   */
  errorCode?: string | null;
  /**
   * 메시지
   */
  message: string;
  /**
   * 상태코드
   */
  statusCode: string;
}

export interface GetCertificateDetailResponse {
  /**
   * 응답데이터
   */
  data: {
    /**
     * 사용자 실명
     */
    realName: string;
    /**
     * UTC 기준 수료증 발급 시각
     */
    certificatedAtUTC: string;
    /**
     * 지공자 이름
     */
    instructorName: string;
    /**
     * 수료증 발급 시각
     */
    certificatedAt: string;
    /**
     * 수강권 ID
     */
    voucherId: number;
    /**
     * UTC 기준 강의 시작 시각
     */
    startedAtUTC: string;
    /**
     * 강의 시작 시각
     */
    startedAt: string;
    /**
     * 강의의 런타임
     */
    runtime: number;
    /**
     * 강의 제목
     */
    title: string;
    /**
     * 강의 ID
     */
    courseId: number;
    /**
     * 사용자 ID
     */
    userId: number;
  };
  /**
   * 에러코드
   */
  errorCode?: string | null;
  /**
   * 메시지
   */
  message: string;
  /**
   * 상태코드
   */
  statusCode: string;
}

export interface AddCartsResponse {
  /**
   * 응답데이터
   */
  data: {
    /**
     * 장바구니에 담긴 강의 목록
     */
    items: {
      /**
       * 장바구니 ID
       */
      cartId: number;
      /**
       * 강의 ID
       */
      courseId: number;
    }[];
  };
  /**
   * 에러코드
   */
  errorCode?: string | null;
  /**
   * 메시지
   */
  message: string;
  /**
   * 상태코드
   */
  statusCode: string;
}

export interface FindMyListCoursesResponse {
  /**
   * 응답데이터
   */
  data: {
    /**
     * 강의폴더에 속한 강의 리스트
     */
    courses: {
      /**
       * 강의에 있는 수업 갯수
       */
      lectureCount: number;
      /**
       * 지공자 이름
       */
      instructorName: string;
      /**
       * 수강 가능 여부
       */
      canStudy: boolean;
      /**
       * 수강권 정보, 없으면 수강권 없음
       */
      voucher?: {
        /**
         * UTC 기준 수강권 만료일
         */
        limitedAtUTC?: string | null;
        /**
         * 강의 진도율
         */
        progress: number;
        /**
         * 마지막으로 수강한 수업 유닛 id, 없으면 수업을 수강 안한 상태
         */
        lastUnitId?: number | null;
        /**
         * 강의에서 수강 완료한 수업 수
         */
        completedLectureCount: number;
        /**
         * 수강권 만료일
         */
        limitedAt?: string | null;
      };
      /**
       * 별점 평균
       */
      rating: number;
      /**
       * 강의 id
       */
      id: number;
      /**
       * 강의 제목
       */
      title: string;
      /**
       * 강의 수강 기간(단위: 월), 없으면 무제한 수강
       */
      expireInMonth?: number | null;
      /**
       * 강의 slug
       */
      slug: string;
      /**
       * 강의 스킬 태그 리스트
       */
      tags: string[];
      /**
       * 썸네일 URL
       */
      thumbnailUrl: string;
    }[];
    /**
     * 강의폴더 id
     */
    id: number;
    /**
     * 강의폴더 제목
     */
    title: string;
  };
  /**
   * 에러코드
   */
  errorCode?: string | null;
  /**
   * 메시지
   */
  message: string;
  /**
   * 상태코드
   */
  statusCode: string;
}

export interface FindClientCartResponse {
  /**
   * 응답데이터
   */
  data: {
    /**
     * 현재 시간 (UTC)
     */
    nowUTC: string;
    /**
     * 현재 시간
     */
    now: string;
    /**
     * 장바구니 아이템 목록
     */
    items: {
      /**
       * 학생 수
       */
      studentCount: number;
      /**
       * 프로모션 여부
       */
      isPromotion: boolean;
      /**
       * 정가
       */
      regularPrice: number;
      /**
       * 할인
       */
      discount?: {
        /**
         * 할인율
         */
        discountRate: number;
        /**
         * 할인 종료일
         */
        endedAt: string;
        /**
         * 할인 금액
         */
        discountPrice: number;
        discountStrategy: 'PERCENT' | 'PRICE';
        /**
         * 할인 ID
         */
        id?: number | null;
        type: 'COURSE' | 'USER' | 'PLATFORM' | 'NONE' | 'COUPON';
        /**
         * 할인 제목
         */
        title: string;
        /**
         * 할인 종료일 (UTC)
         */
        endedAtUTC: string;
        /**
         * USD 할인 금액
         */
        usdDiscountPrice: number;
      };
      /**
       * KRW 결제 가격
       */
      krwPaymentPrice: number;
      /**
       * 좋아요 수
       */
      likeCount: number;
      type: 'ON' | 'OFF' | 'CHALLENGE';
      /**
       * 강의 제목
       */
      title: string;
      /**
       * 스킬 목록
       */
      skills: {
        /**
         * 스킬 ID
         */
        id: number;
        /**
         * 스킬 제목
         */
        title: string;
        /**
         * 스킬 슬러그
         */
        slug: string;
      }[];
      /**
       * 강의 시간(분)
       */
      duration?: number | null;
      /**
       * KDT 가격
       */
      kdtPrice?: number | null;
      /**
       * 리뷰 수
       */
      reviewCount: number;
      /**
       * 화폐
       */
      currency: string;
      /**
       * 카테고리 목록
       */
      categories: {
        /**
         * 강의에 연결된 카테고리의 부모카테고리
         */
        parent: {
          /**
           * 부모 카테고리 ID
           */
          id: number;
          /**
           * 부모 카테고리 제목
           */
          title: string;
          /**
           * 부모 카테고리 슬러그
           */
          slug: string;
        };
        /**
         * 강의에 연결된 카테고리 ID
         */
        id: number;
        /**
         * 강의에 연결된 카테고리 이름
         */
        title: string;
        /**
         * 강의에 연결된 카테고리 슬러그
         */
        slug: string;
      }[];
      /**
       * 장바구니 아이템 ID
       */
      id: number;
      /**
       * 강의 ID
       */
      courseId: number;
      /**
       * USD 정가
       */
      usdRegularPrice: number;
      /**
       * 강의 슬러그
       */
      slug: string;
      /**
       * 리뷰 평균
       */
      reviewAverage: number;
      /**
       * 강의 업데이트일 (UTC)
       */
      updatedAtUTC?: string | null;
      /**
       * 강의 업데이트일
       */
      updatedAt?: string | null;
      /**
       * 썸네일 URL
       */
      thumbnailUrl: string;
      /**
       * 강의 공개일 (UTC)
       */
      publishedAtUTC?: string | null;
      /**
       * 강사 이름
       */
      instructorName: string;
      /**
       * 레벨
       */
      level?: {
        code?: 'Beginner' | 'Basic' | 'Intermediate' | 'CodeNotExists';
        /**
         * 레벨 ID
         */
        id?: number | null;
        /**
         * 레벨 제목
         */
        title?: string | null;
      };
      /**
       * 강의 공개일
       */
      publishedAt?: string | null;
      /**
       * USD 결제 가격
       */
      usdPaymentPrice: number;
      /**
       * KRW 정가
       */
      krwRegularPrice: number;
      /**
       * 결제 가격
       */
      paymentPrice: number;
      /**
       * 챌린지 강의 정보
       */
      challenge?: {
        /**
         * 챌린지 모집 시작시간
         */
        applyStartedAtUTC: string;
        /**
         * 챌린지 시작시간
         */
        startedAtUTC: string;
        /**
         * 챌린지 종료시간
         */
        endedAtUTC: string;
      };
      /**
       * 강사 ID
       */
      instructorId: number;
    }[];
  };
  /**
   * 에러코드
   */
  errorCode?: string | null;
  /**
   * 메시지
   */
  message: string;
  /**
   * 상태코드
   */
  statusCode: string;
}

export interface AddCartsRequest {
  /**
   * 페이스북 Event ID
   */
  eventId?: string | null;
  /**
   * 페이스북 클릭 ID
   */
  fbclid?: string | null;
  /**
   * 장바구니에 담을 강의 ID 목록
   */
  courseIds: number[];
}

export interface UpdateMyListRequest {
  /**
   * 수정할 강의폴더 id
   */
  id: number;
  /**
   * 수정할 제목
   */
  title: string;
}

export interface RoadmapsByAuthorResponse {
  /**
   * 응답데이터
   */
  data: {
    /**
     * 현재 페이지
     */
    pageNumber: number;
    /**
     * 전체 페이지
     */
    totalPage: number;
    /**
     * 페이지 요청 크기
     */
    pageSize: number;
    /**
     * 전체 개수
     */
    totalCount: number;
    items?: {
      /**
       * 로드맵 생성자 썸네일 URL
       */
      authorThumbnailUrl: string;
      /**
       * 로드맵 커버 이미지 URL(DEPRECATED)
       */
      coverFileUrl: string;
      /**
       * 로드맵 생성자 이름
       */
      authorName: string;
      /**
       * 좋아요수
       */
      likeCount: number;
      /**
       * 로드맵 아이디
       */
      id: number;
      /**
       * 로드맵 생성자 이름(DEPRECATED)
       */
      userName: string;
      /**
       * 참여자수
       */
      enrollCount: number;
      /**
       * 로드맵 제목
       */
      title: string;
      /**
       * 로드맵 썸네일 이미지 URL
       */
      thumbnailUrl: string;
    }[];
  };
  /**
   * 에러코드
   */
  errorCode?: string | null;
  /**
   * 메시지
   */
  message: string;
  /**
   * 상태코드
   */
  statusCode: string;
}

export interface FindMyListResponse {
  /**
   * 응답데이터
   */
  data: {
    /**
     * 강의폴더에 들어있는 강의 수
     */
    courseCount: number;
    /**
     * 강의폴더 id
     */
    id: number;
    /**
     * 강의폴더 제목
     */
    title: string;
  }[];
  /**
   * 에러코드
   */
  errorCode?: string | null;
  /**
   * 메시지
   */
  message: string;
  /**
   * 상태코드
   */
  statusCode: string;
}

export const enum Sort {
  CERTIFICATED = 'CERTIFICATED',
  CONFIRMED = 'CONFIRMED',
}

export const enum Schema {
  CERTIFICATED = 'CERTIFICATED',
  CONFIRMED = 'CONFIRMED',
}

export const enum ChargeType {
  FREE = 'FREE',
  PAID = 'PAID',
  ALL = 'ALL',
}

export const enum CourseType {
  ON = 'ON',
  OFF = 'OFF',
  CHALLENGE = 'CHALLENGE',
}

export const enum Studying {
  STUDYING = 'STUDYING',
  COMPLETED = 'COMPLETED',
}

/** CourseStatus */
export const enum CourseStatus {
  DRAFT = 'DRAFT',
  PRIVATE = 'PRIVATE',
  SUBMIT = 'SUBMIT',
  PUBLISH = 'PUBLISH',
  PAUSE = 'PAUSE',
  PUBLISH_READY = 'PUBLISH_READY',
}

/** OrderMode */
export const enum OrderMode {
  NORMAL = 'NORMAL',
  B2B_CODE = 'B2B_CODE',
  B2C_CODE = 'B2C_CODE',
  CODE = 'CODE',
  MENTOR = 'MENTOR',
  MENTORING = 'MENTORING',
  COURSE_GIFT = 'COURSE_GIFT',
  CLIP = 'CLIP',
}

/** OrderStatus */
export const enum OrderStatus {
  CANCEL_DELETE = 'CANCEL_DELETE',
  MENTEE_CANCEL = 'MENTEE_CANCEL',
  PARTIAL_CANCEL = 'PARTIAL_CANCEL',
  CANCEL_NOT_ENOUGH_PROGRESS = 'CANCEL_NOT_ENOUGH_PROGRESS',
  GROUP_PAYMENT_CANCEL = 'GROUP_PAYMENT_CANCEL',
  PAYMENT_CANCEL = 'PAYMENT_CANCEL',
  ORDER_FORM = 'ORDER_FORM',
  CONFLICT_CANCEL = 'CONFLICT_CANCEL',
  AUTO_CANCEL = 'AUTO_CANCEL',
  PAYMENT_READY = 'PAYMENT_READY',
  TOTAL_CANCEL = 'TOTAL_CANCEL',
  PAYMENT_FAIL = 'PAYMENT_FAIL',
  PAYMENT_STOP = 'PAYMENT_STOP',
  CODE_ORDER = 'CODE_ORDER',
  ORDER_CANCEL = 'ORDER_CANCEL',
  PAYMENT_COMPLETE = 'PAYMENT_COMPLETE',
  PAYMENT_COMPLETE_PROCESSING = 'PAYMENT_COMPLETE_PROCESSING',
  ADMIN_CANCEL = 'ADMIN_CANCEL',
  MENTOR_CANCEL = 'MENTOR_CANCEL',
  DEPOSIT_WAIT = 'DEPOSIT_WAIT',
  WC_COMPLETED = 'WC_COMPLETED',
  MANUAL_DELETE = 'MANUAL_DELETE',
  REFUND_REQUEST = 'REFUND_REQUEST',
}

/** CourseGiftResponseStatus */
export const enum CourseGiftResponseStatus {
  RECEIVED = 'RECEIVED',
  CANCELED = 'CANCELED',
}

/** CourseLevelTypeCode */
export const enum CourseLevelTypeCode {
  Beginner = 'Beginner',
  Basic = 'Basic',
  Intermediate = 'Intermediate',
  CodeNotExists = 'CodeNotExists',
}

/** CourseCardTag */
export const enum CourseCardTag {
  IS_ACTIVE_IN_QUESTION = 'IS_ACTIVE_IN_QUESTION',
  IS_B2B_SUBSCRIPTION = 'IS_B2B_SUBSCRIPTION',
  HAS_MISSION = 'HAS_MISSION',
  IS_CHALLENGE_APPLYING = 'IS_CHALLENGE_APPLYING',
  IS_CHALLENGE_BEFORE_APPLY = 'IS_CHALLENGE_BEFORE_APPLY',
  IS_CHALLENGE_AFTER_APPLY = 'IS_CHALLENGE_AFTER_APPLY',
  IS_CHALLENGE_PROCESSING = 'IS_CHALLENGE_PROCESSING',
  IS_CHALLENGE_COMPLETED = 'IS_CHALLENGE_COMPLETED',
  IS_CHALLENGE_PARTICIPATING = 'IS_CHALLENGE_PARTICIPATING',
}

/** OfflineCourseType */
export const enum OfflineCourseType {
  CONFERENCE = 'CONFERENCE',
  CLASS = 'CLASS',
}

/** MarketingPartnerType */
export const enum MarketingPartnerType {
  BUSINESS = 'BUSINESS',
  PERSONAL = 'PERSONAL',
}

/** CourseGiftStatus */
export const enum CourseGiftStatus {
  READY_TO_RECEIVE = 'READY_TO_RECEIVE',
  RECEIVED = 'RECEIVED',
  EXPIRED = 'EXPIRED',
  CANCELED = 'CANCELED',
}

/** CancelType */
export const enum CancelType {
  B2B = 'B2B',
  B2C = 'B2C',
}

/** VoucherType */
export const enum VoucherType {
  LEGACY = 'LEGACY',
  IS_ADMIN = 'IS_ADMIN',
  BY_ADMIN = 'BY_ADMIN',
  IS_INSTRUCTOR = 'IS_INSTRUCTOR',
  BY_INSTRUCTOR = 'BY_INSTRUCTOR',
  FREE = 'FREE',
  ORDER = 'ORDER',
  CODE = 'CODE',
  GROUP_ORDER = 'GROUP_ORDER',
  GROUP_SUBSCRIPTION = 'GROUP_SUBSCRIPTION',
  COURSE_GIFT = 'COURSE_GIFT',
}

/** DiscountStrategy */
export const enum DiscountStrategy {
  PERCENT = 'PERCENT',
  PRICE = 'PRICE',
}

/** DiscountType */
export const enum DiscountType {
  COURSE = 'COURSE',
  USER = 'USER',
  PLATFORM = 'PLATFORM',
  NONE = 'NONE',
  COUPON = 'COUPON',
}
